/**
 * Чистая половина привязки ленты движений к складским задачам FBS: КАК строка
 * ленты называет задачу и чем «задачи нет» отличается от «спросить было нечем».
 *
 * Здесь нет ни React, ни DOM. Три состояния одной строки — вся суть раздела, и
 * их обязан проверять тест, а не глаз на живом экране: DOM-тестов в проекте нет.
 */

import type {
  FbsTaskBody,
  FbsTaskStep,
  MovePoint,
  MoveTask,
} from "@/lib/providers";

/** Адрес карточки складской задачи. Номер НАШ (`fbs.fbs_task.id`). */
export function taskHref(id: number): string {
  return `/fbs-task/${id}`;
}

/**
 * Что показать в строке ленты. Ровно три исхода, и путать их нельзя:
 *
 *     task     переезд сделала складская задача — её номер и роль
 *     manual   задачи нет: вещь двигал человек руками
 *     unknown  базу задач не спрашивали (песочная полоса, отказ базы)
 *
 * Третий появился не для полноты. Без него пустая привязка при недоступном
 * соседе выглядела бы как ручной переезд, и экран уверенно утверждал бы то,
 * чего не знает, — на разборе «кто положил вещь в короб» это ответ ровно
 * наоборот.
 */
export type MoveMark =
  | {
      kind: "task";
      task: MoveTask;
      label: string;
      /** Вторая строка ячейки: «ВБ · В доставке». `null` — статуса нет. */
      mp: string | null;
      title: string;
      href: string;
    }
  | { kind: "manual" }
  | { kind: "unknown" };

/**
 * Подпись задачи в строке: «1213 · упаковка».
 *
 * Номер первым, потому что им задачу и зовут; роль второй, потому что она
 * отвечает на вопрос, с которым в ленту и приходят, — «положили или взяли».
 * Статус СКЛАДСКОЙ задачи сюда не идёт: у строки ленты он почти всегда один и
 * тот же («Упаковано»), а места в ячейке ровно на одну добавку — и она отдана
 * статусу площадки (`mpLabel`), который на этот вопрос отвечает по-разному.
 */
export function taskLabel(task: MoveTask): string {
  return `${task.id} · ${task.role_name}`;
}

/**
 * Вторая строка ячейки: «ВБ · В доставке». `null` — статуса площадки нет.
 *
 * Площадка названа ВСЕГДА, когда задание прочитано, и это суть замечания
 * владельца 09.09.26: «Упаковано» — это то, что сделал СКЛАД, и без имени
 * площадки рядом второй статус читался бы как продолжение первого. Пустой
 * статус при живом задании оставляет одно имя площадки — оно само по себе
 * ответ («это ВБ»), а прочерк рядом с ним ничего не добавляет.
 *
 * Имя статуса берётся из справочника площадки, код — когда имени нет: у Ozon
 * имён в зеркале не лежит вовсе, и «delivering» честнее выдуманного перевода.
 */
export function mpLabel(task: MoveTask): string | null {
  const mp = task.mp;
  if (!mp) return null;
  const status = mp.status_name ?? mp.status;
  return status ? `${mp.platform_name} · ${status}` : mp.platform_name;
}

/**
 * Подсказка на наведении: задача, её статус и обе половины статуса площадки.
 *
 * Подстатус живёт ЗДЕСЬ, а не в строке: «Сборочное задание отсортировано»
 * длиннее самой колонки, а вопрос, с которым в ленту приходят, — «взяли или
 * положили», и на него отвечает первая строка.
 *
 * Статус берётся ИМЕНЕМ из справочника соседа (`fbs_task_status`), а код —
 * только когда имени нет: «packed» на экране кладовщика ничего не значит.
 */
export function taskTitle(task: MoveTask): string {
  const mp = task.mp;
  const parts = [
    `Задача ${task.id}`,
    task.role_name,
    task.status_name ?? task.status,
    mp ? `${mp.platform_name}: ${mp.status_name ?? mp.status ?? "—"}` : null,
    mp ? (mp.substatus_name ?? mp.substatus) : null,
  ];
  return parts.filter(Boolean).join(" · ");
}

/** Разбор строки ленты в один из трёх исходов. */
export function moveMark(
  task: MoveTask | null | undefined,
  tasksKnown: boolean,
): MoveMark {
  if (task) {
    return {
      kind: "task",
      task,
      label: taskLabel(task),
      mp: mpLabel(task),
      title: taskTitle(task),
      href: taskHref(task.id),
    };
  }
  return tasksKnown ? { kind: "manual" } : { kind: "unknown" };
}

/**
 * Номер задачи из того, что набрал человек. `null` — набрано не то.
 *
 * Только цифры, и это не строгость ради строгости: поле поиска раздела
 * принимает номер задачи, а «1213 » со сканера или скопированное «№1213»
 * иначе ушло бы в адрес как есть и вернулось бы 422 вместо карточки.
 */
export function taskNumber(text: string): number | null {
  const digits = text.trim().replace(/^№/, "").trim();
  if (!/^\d+$/.test(digits)) return null;
  const value = Number(digits);
  return value > 0 ? value : null;
}

/**
 * Как зовётся половина задачи в заголовке блока: «Подбор», «Упаковка».
 *
 * Имя роли приезжает от сервиса строчными («подбор») — тем же словом, что в
 * строке ленты, — и заглавная буква ставится ЗДЕСЬ, а не вторым полем в
 * ответе: два написания одного слова разошлись бы на первой правке.
 */
export function stepTitle(roleName: string): string {
  const text = roleName.trim();
  if (!text) return "";
  return text.slice(0, 1).toUpperCase() + text.slice(1);
}

/**
 * Оба конца ПЕРЕМЕЩЕНИЯ половины задачи: откуда взяли и куда положили.
 *
 * Замечание владельца 09.09.26: у перемещения есть «откуда» и «куда», а
 * карточка показывала по одному концу на половину — «Откуда» у подбора,
 * «Куда» у упаковки. Куда подбор ПОЛОЖИЛ вещь и откуда упаковка её ВЗЯЛА,
 * экран не говорил вовсе, хотя оба конца стоят в самом перемещении.
 *
 * Источника два, и порядок между ними несущий. Первый — само перемещение из
 * CRM: оно знает обе половины. Второй — `place` из учёта сборки, и он знает
 * ровно ОДИН конец: у подбора это место, ОТКУДА сняли, у упаковки — место,
 * КУДА уложили. Поэтому запасной ответ половинчатый, и это честно: пустое
 * «куда» при недоступной CRM значит «не знаем», а не «никуда».
 */
export function stepEnds(step: FbsTaskStep): {
  from: MovePoint | null;
  to: MovePoint | null;
} {
  if (step.move) return { from: step.move.from, to: step.move.to };
  return step.role === "pick"
    ? { from: step.place, to: null }
    : { from: null, to: step.place };
}

/**
 * Серия, которая ехала ЭТИМ перемещением. `null` — перемещение не прочитано.
 *
 * Берётся из самого перемещения (`sMove.serial`), а не из задачи, и это
 * требование владельца 09.09.26: карточка обязана назвать, что именно
 * переехало, значением перемещения. Совпадение с серией задачи — свойство
 * данных, а не то, на что опирается экран.
 */
export function moveSerial(step: FbsTaskStep): number | null {
  return step.move?.serial ?? null;
}

/**
 * Разошлась ли серия ЗНАКА с серией задачи.
 *
 * Не ошибка и не редкость: наклейку клеит человек, автоподбора знака по серии
 * в сборке нет намеренно. Замер 09.09.26 — у 708 задач из 1 273 со знаком
 * серии разные, и во всех 708 случаях товар ТОТ ЖЕ; знака от чужого товара нет
 * ни одного. Экран поэтому не кричит об ошибке, а просто показывает обе серии:
 * до этой правки номер знака стоял один, и расхождение было невидимо.
 *
 * `false`, пока какой-нибудь из двух серий нет: «не знаем» — не «разошлось».
 */
export function znakMismatch(task: FbsTaskBody): boolean {
  if (!task.znak_serial || !task.serial) return false;
  return task.znak_serial !== task.serial;
}
